import { makeStyles } from '@material-ui/core';
import moment from 'moment-timezone';
import { WeeklyHoursEntry } from 'utils/wemble-api.generated';

const useStyles = makeStyles((theme) => ({
  widget: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '12px 14px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  avg: {
    fontSize: 11,
    color: theme.palette.gray.g2,
    fontWeight: 500,
  },
  trend: {
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  barsRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'flex-end',
    height: 80,
  },
  barColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    height: '100%',
    justifyContent: 'flex-end',
  },
  barStack: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-end',
  },
  barBillable: {
    backgroundColor: '#6B7FA3',
  },
  barNonBillable: {
    backgroundColor: '#6B7FA3',
    opacity: 0.45,
  },
  barLeave: {
    backgroundColor: '#DBDBDB',
  },
  labelsRow: {
    display: 'flex',
    gap: 6,
    marginTop: 6,
  },
  barLabel: {
    flex: 1,
    textAlign: 'center' as const,
  },
  barTotal: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  barWeek: {
    fontSize: 8,
    color: theme.palette.gray.g2,
    marginTop: 1,
    textTransform: 'uppercase' as const,
    marginLeft: -5,
    marginRight: -5,
    whiteSpace: 'nowrap' as const,
  },
  legend: {
    display: 'flex',
    gap: 12,
    marginTop: 10,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    color: theme.palette.gray.g2,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 2,
  },
}));

const formatWeekRange = (weekStart: string): string => {
  const start = moment(weekStart);
  const end = start.clone().add(6, 'days');

  if (start.month() === end.month()) {
    return `${start.format('D')}-${end.format('D')} ${start.format('MMM')}`;
  }
  return `${start.format('D MMM')}-${end.format('D MMM')}`;
};

const WeeklyHoursWidget = ({ weeks }: { weeks: WeeklyHoursEntry[] }) => {
  const classes = useStyles();

  const hasLeave = weeks.some(w => w.leaveHours > 0);
  const maxHours = Math.max(...weeks.map(w => w.totalHours), 1);
  const nonZeroWeeks = weeks.filter(w => w.totalHours > 0);
  const avgHours = nonZeroWeeks.length > 0 ? nonZeroWeeks.reduce((sum, w) => sum + w.totalHours, 0) / nonZeroWeeks.length : 0;
  const prevWeekHours = weeks.length >= 2 ? weeks[weeks.length - 2].totalHours - weeks[weeks.length - 2].leaveHours : 0;
  const currWeekHours = weeks.length >= 2 ? weeks[weeks.length - 1].totalHours - weeks[weeks.length - 1].leaveHours : 0;
  const trendPercent = prevWeekHours > 0
    ? ((currWeekHours - prevWeekHours) / prevWeekHours) * 100
    : 0;

  return (
    <div className={classes.widget}>
      <div className={classes.header}>
        <span className={classes.avg}>Avg {avgHours.toFixed(1)}h/week</span>
        {weeks.length >= 2 && Math.abs(trendPercent) >= 1 && (
          <span className={classes.trend} style={{ color: trendPercent > 0 ? '#27ae60' : undefined }}>
            {trendPercent > 0 ? '\u2191' : '\u2193'}{Math.abs(trendPercent) <= 100 ? ` ${Math.abs(trendPercent).toFixed(0)}%` : ''}
          </span>
        )}
      </div>
      <div className={classes.barsRow}>
        {weeks.map((week) => {
          const leaveHrs = week.leaveHours || 0;
          const nonBillableExLeave = week.nonBillableHours - leaveHrs;
          const billableRatio = week.totalHours > 0 ? (week.billableHours / week.totalHours) * 100 : 0;
          const leaveRatio = week.totalHours > 0 ? (leaveHrs / week.totalHours) * 100 : 0;
          const nonBillableRatio = week.totalHours > 0 ? (nonBillableExLeave / week.totalHours) * 100 : 0;
          const barHeight = (week.totalHours / maxHours) * 100;

          return (
            <div className={classes.barColumn} key={week.weekStart}>
              <div className={classes.barStack} style={{ height: `${barHeight}%` }}>
                {leaveHrs > 0 && (
                  <div className={classes.barLeave} style={{ height: `${leaveRatio}%` }} />
                )}
                {nonBillableExLeave > 0 && (
                  <div className={classes.barNonBillable} style={{ height: `${nonBillableRatio}%` }} />
                )}
                <div className={classes.barBillable} style={{ height: `${billableRatio}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.labelsRow}>
        {weeks.map((week) => (
          <div className={classes.barLabel} key={`label-${week.weekStart}`}>
            <div className={classes.barTotal}>{week.totalHours.toFixed(1)}</div>
            <div className={classes.barWeek}>{formatWeekRange(week.weekStart)}</div>
          </div>
        ))}
      </div>
      {weeks.length > 0 && (
        <div className={classes.legend}>
          <div className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: '#6B7FA3' }} />
            Billable
          </div>
          <div className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: '#6B7FA3', opacity: 0.45 }} />
            Non-billable
          </div>
          {hasLeave && (
            <div className={classes.legendItem}>
              <div className={classes.legendDot} style={{ backgroundColor: '#DBDBDB' }} />
              Away
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeeklyHoursWidget;
